/**
 * Dashboard primitives.
 *
 * Tiny reusable bits the new ``DashboardPage`` is built from. Visual
 * language lifted from the marketing website: rounded surfaces,
 * subtle borders, lime accent on primaries, light glow on KPI strips.
 *
 *   * Surface       — generic glass-like card with a 1px border.
 *   * KPICard       — Surface tuned for the four hero KPIs.
 *   * Sparkline     — inline SVG line + area-fill, tinted by tone.
 *   * AreaChart24h  — wider 24-hour area chart with smooth curves.
 *   * StackedDayBars — 7-day stacked bars (succeeded / failed / running).
 *   * StatusDot     — 8px filled dot tinted by tone.
 *   * format helpers (numbers, durations, relative time).
 */

import React from 'react';
import { Box, Stack, Typography, Tooltip, IconButton, alpha, useTheme } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';


// ---------------------------------------------------------------------------
// Surface — base glass card
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ShortcutButton — uniform "open this page" affordance, pinned to the
// top-right corner of any Surface (same size + offset everywhere).
// ---------------------------------------------------------------------------

export const ShortcutButton = ({ onClick, title = 'Open' }) => (
  <Tooltip title={title} arrow>
    <IconButton
      className="dash-shortcut"
      size="small"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        p: '4px',
        zIndex: 2,
        opacity: 0.4,
        color: 'text.secondary',
        transition: 'opacity 160ms ease, color 160ms ease',
        '&:hover': { opacity: 1, color: 'text.primary' },
      }}
    >
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </IconButton>
  </Tooltip>
);


export const Surface = ({ sx = {}, children, ...rest }) => (
  <Box
    sx={{
      position: 'relative',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: (t) => t.palette.mode === 'dark'
        ? alpha(t.palette.background.paper, 0.7)
        : alpha(t.palette.background.paper, 0.85),
      backdropFilter: 'saturate(140%) blur(8px)',
      WebkitBackdropFilter: 'saturate(140%) blur(8px)',
      transition: 'border-color 200ms ease, box-shadow 200ms ease',
      '&:hover': {
        borderColor: (t) => alpha(t.palette.primary.main, 0.35),
      },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);


// ---------------------------------------------------------------------------
// KPICard — hero metric with sparkline
// ---------------------------------------------------------------------------

export const KPICard = ({
  tone = 'neutral',
  icon, label, primary, subtitle,
  sparkline, footer,
  onShortcut, shortcutTitle = 'Open',
}) => {
  const theme = useTheme();
  const accent = toneColor(theme, tone);

  return (
    <Surface
      onClick={onShortcut}
      sx={{
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        cursor: onShortcut ? 'pointer' : 'default',
        // Inner glow strip on the top edge — picks up the tone color.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, right: 0, top: 0,
          height: 2,
          background: tone !== 'neutral'
            ? `linear-gradient(90deg, transparent, ${accent} 40%, ${accent} 60%, transparent)`
            : 'transparent',
          opacity: 0.7,
        },
        // Brighten the shortcut icon when hovering anywhere on the card.
        '&:hover .dash-shortcut': { opacity: 1 },
      }}
    >
      {onShortcut && (
        <ShortcutButton onClick={onShortcut} title={shortcutTitle} />
      )}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        <Box
          sx={{
            width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 1,
            bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.18 : 0.10),
            color: accent,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="overline"
          sx={{
            fontSize: 10,
            color: 'text.secondary',
            fontWeight: 600,
            letterSpacing: 1,
          }}
        >
          {label}
        </Typography>
      </Stack>

      <Typography
        component="div"
        sx={{
          fontFamily: 'Lexend Variable, Arial',
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: -0.5,
          lineHeight: 1.1,
          color: 'text.primary',
          mb: 0.5,
        }}
      >
        {primary}
      </Typography>

      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: sparkline ? 1.25 : 0 }}>
          {subtitle}
        </Typography>
      )}

      {sparkline && (
        <Box sx={{ height: 36, mt: 0.5 }}>
          {sparkline}
        </Box>
      )}

      {footer && (
        <Box sx={{ mt: 1 }}>
          {footer}
        </Box>
      )}
    </Surface>
  );
};


// ---------------------------------------------------------------------------
// Sparkline — small SVG line + area fill
// ---------------------------------------------------------------------------

export const Sparkline = ({ data = [], tone = 'neutral', height = 36 }) => {
  const theme = useTheme();
  const color = toneColor(theme, tone === 'neutral' ? 'primary' : tone);
  const points = Array.isArray(data) ? data : [];

  if (points.length === 0) {
    return (
      <Box
        sx={{
          height,
          width: '100%',
          borderRadius: 1,
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
          No data yet
        </Typography>
      </Box>
    );
  }

  // Pad single-point series so we still draw a smooth line.
  const series = points.length === 1 ? [0, points[0]] : points;
  const max = Math.max(1, ...series);
  const w = 100;
  const h = 100;
  const stepX = series.length > 1 ? w / (series.length - 1) : 0;

  const linePath = series.map((v, i) => {
    const x = i * stepX;
    const y = h - (v / max) * h * 0.92 - 4;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');

  const areaPath = `${linePath} L ${(series.length - 1) * stepX} ${h} L 0 ${h} Z`;

  const gradId = `spark-${tone}-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.45" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};


// ---------------------------------------------------------------------------
// AreaChart24h — bigger area chart for the 24-hour inference traffic
// ---------------------------------------------------------------------------

export const AreaChart24h = ({ data = [] }) => {
  const theme = useTheme();
  const color = theme.palette.primary.main;

  // Normalize to 24 buckets so the chart always renders with the same
  // x-axis. Empty buckets render as zeros.
  const buckets = Array.from({ length: 24 }, () => 0);
  const labels  = Array.from({ length: 24 }, () => '');
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const t = new Date(now.getTime() - (23 - i) * 3600 * 1000);
    labels[i] = t.toLocaleTimeString([], { hour: 'numeric', hour12: true });
  }
  for (const p of data) {
    if (!p?.ts) continue;
    const ts = new Date(p.ts);
    const diffH = Math.floor((now.getTime() - ts.getTime()) / (3600 * 1000));
    if (diffH >= 0 && diffH < 24) {
      buckets[23 - diffH] += Number(p.count || 0);
    }
  }

  const max = Math.max(1, ...buckets);
  const w = 800;
  const h = 200;
  const padX = 12;
  const padY = 16;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const stepX = innerW / 23;

  // Build a Catmull-Rom-ish smoothed path using bezier curves between
  // each pair of points — gives the area chart a soft, modern look
  // without an external lib.
  const ptX = (i) => padX + i * stepX;
  const ptY = (v) => padY + innerH - (v / max) * innerH;

  let linePath = '';
  for (let i = 0; i < buckets.length; i++) {
    const x = ptX(i);
    const y = ptY(buckets[i]);
    if (i === 0) {
      linePath += `M ${x} ${y}`;
    } else {
      const px = ptX(i - 1);
      const py = ptY(buckets[i - 1]);
      const cx1 = px + stepX * 0.5;
      const cy1 = py;
      const cx2 = x  - stepX * 0.5;
      const cy2 = y;
      linePath += ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x} ${y}`;
    }
  }
  const areaPath = `${linePath} L ${ptX(23)} ${padY + innerH} L ${ptX(0)} ${padY + innerH} Z`;

  const gradId = `area24-${theme.palette.mode}`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.5" />
          <stop offset="60%"  stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Horizontal grid lines (4 thin guides) */}
      {[0.25, 0.5, 0.75, 1].map((p, i) => {
        const y = padY + innerH * (1 - p);
        return (
          <line
            key={i}
            x1={padX} x2={w - padX}
            y1={y} y2={y}
            stroke={alpha(theme.palette.text.primary, 0.05)}
            strokeWidth="1"
            strokeDasharray="2 4"
          />
        );
      })}

      {/* Area + line */}
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Glow halo on the line */}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeOpacity="0.35"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="blur(4px)"
      />

      {/* X-axis labels: only 0h, 6h, 12h, 18h, 24h */}
      {[0, 6, 12, 18, 23].map((i) => (
        <text
          key={i}
          x={ptX(i)}
          y={h - 2}
          fontSize="10"
          fill={alpha(theme.palette.text.secondary, 0.7)}
          textAnchor="middle"
        >
          {labels[i]}
        </text>
      ))}
    </svg>
  );
};


// ---------------------------------------------------------------------------
// StackedDayBars — the 7-day training velocity strip
// ---------------------------------------------------------------------------

export const StackedDayBars = ({ data = [] }) => {
  const theme = useTheme();
  const succColor = theme.palette.primary.main;
  const failColor = theme.palette.error.main;
  const runColor  = alpha(theme.palette.text.primary, 0.18);

  // Pad / normalise to 7 buckets.
  const days = data.slice(-7);
  while (days.length < 7) days.unshift({ date: '', succeeded: 0, failed: 0, running: 0 });
  const max = Math.max(
    1,
    ...days.map((d) => (d.succeeded || 0) + (d.failed || 0) + (d.running || 0)),
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.5,
        height: '100%',
        width: '100%',
      }}
    >
      {days.map((d, i) => {
        const total = (d.succeeded || 0) + (d.failed || 0) + (d.running || 0);
        const ratio = total / max;
        const barH  = Math.max(2, ratio * 100); // %
        const sucP  = total ? (d.succeeded / total) * 100 : 0;
        const failP = total ? (d.failed    / total) * 100 : 0;
        const runP  = total ? (d.running   / total) * 100 : 0;
        const dayLabel = d.date
          ? new Date(d.date).toLocaleDateString([], { weekday: 'short' })[0]
          : '';
        return (
          <Box
            key={i}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              height: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'flex-end',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: `${barH}%`,
                  borderRadius: 0.75,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  transition: 'height 600ms ease',
                }}
              >
                {failP > 0 && (
                  <Box sx={{ height: `${failP}%`, bgcolor: failColor }} />
                )}
                {runP > 0 && (
                  <Box sx={{ height: `${runP}%`, bgcolor: runColor }} />
                )}
                {sucP > 0 && (
                  <Box sx={{ height: `${sucP}%`, bgcolor: succColor }} />
                )}
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1, fontWeight: 600 }}
            >
              {dayLabel}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// StatusDot
// ---------------------------------------------------------------------------

export const StatusDot = ({ tone = 'neutral', size = 8 }) => {
  const theme = useTheme();
  const color = toneColor(theme, tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: color,
        boxShadow: `0 0 8px ${alpha(color, 0.5)}`,
      }}
    />
  );
};


// ---------------------------------------------------------------------------
// Tone resolver — maps a "tone" key to a real palette color
// ---------------------------------------------------------------------------

function toneColor(theme, tone) {
  switch (tone) {
    case 'success': return theme.palette.success.main;
    case 'warning': return theme.palette.warning?.main || '#f59e0b';
    case 'error':   return theme.palette.error.main;
    case 'primary':
    case 'neutral':
    default:        return theme.palette.primary.main;
  }
}


// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

export const formatNumber = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
  if (Number.isInteger(v))      return v.toLocaleString();
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const formatDuration = (seconds) => {
  const s = Number(seconds) || 0;
  if (s <= 0) return '—';
  if (s < 60)        return `${s.toFixed(0)}s`;
  if (s < 3600)      return `${(s / 60).toFixed(0)}m`;
  if (s < 86_400)    return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86_400).toFixed(1)}d`;
};

export const formatRelativeTime = (date) => {
  const ms = Date.now() - new Date(date).getTime();
  if (ms < 60_000)              return 'just now';
  if (ms < 3_600_000)           return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000)          return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};
